import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@kop/ui';
import { X, RefreshCw } from 'lucide-react';
import { FeatureLoading } from '../feature-skeleton';
import { loadMonthlyMeeting, peekMonthlyMeeting, type MonthlyMeetingRead } from './monthly-meeting-read';
import { loadHeadquartersMeetingSites } from '../communications/meeting-minutes-repository';
import { onSensitiveMemoryCacheClear } from '../../runtime/sensitive-cache-lifecycle';
import './headquarters-dashboard.css';

const sections = [
  ['summary', '월회의 요약'],
  ['plan', '계획 이행'],
  ['risk', '위험요인·개선'],
  ['budget', '예산 집행'],
  ['voice', '종사자 의견'],
  ['accident', '사고·후속조치'],
  ['education', '교육 현황'],
  ['cases', '주요 개선사례'],
  ['actions', '회의 후속조치'],
] as const;
const count = (v: number | undefined | null) =>
  v == null ? '확인 필요' : v.toLocaleString('ko-KR');
const cacheKey = (uid: string, siteId: string | null, month: string) => `${uid}:${siteId ?? 'no-site'}:${month}`;
export function MeetingTable({
  headings,
  rows,
}: {
  headings: readonly string[];
  rows: readonly (readonly React.ReactNode[])[];
}) {
  return (
    <div className="hq-table" data-bounded-content>
      <table>
        <thead>
          <tr>
            {headings.map((h) => (
              <th key={h}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i}>
              {row.map((cell, j) => (
                <td key={j}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
export function MonthlyMeetingReview({
  uid,
  siteId,
  initialMonth,
  onClose,
  onOpen,
  featureIds,
}: {
  uid: string;
  siteId: string | null;
  initialMonth: string;
  onClose: () => void;
  onOpen: (id: string) => void;
  featureIds: readonly string[];
}) {
  const [selectedSiteId, setSelectedSiteId] = useState<string | null>(siteId);
  const [siteChoices, setSiteChoices] = useState<readonly Readonly<{ siteId: string; displayName: string }>[]>([]);
  const [sitesError, setSitesError] = useState(false);
  const [month, setMonth] = useState(initialMonth),
    [section, setSection] = useState<string>('summary'),
    [data, setData] = useState<MonthlyMeetingRead | null>(() => peekMonthlyMeeting(uid, selectedSiteId, initialMonth)),
    [busy, setBusy] = useState(() => !peekMonthlyMeeting(uid, selectedSiteId, initialMonth)),
    [revision, setRevision] = useState(0),
    [meetingId, setMeetingId] = useState('');
  const panel = useRef<HTMLElement>(null);
  const lastHandledRevision = useRef(revision);
  const visibleReadKey = useRef<string | null>(peekMonthlyMeeting(uid, selectedSiteId, initialMonth) ? cacheKey(uid, selectedSiteId, initialMonth) : null);
  useEffect(() => {
    if (siteId) return;
    let active = true;
    void loadHeadquartersMeetingSites().then((sites) => {
      if (!active) return;
      setSiteChoices(sites);
      setSelectedSiteId((current) => current ?? sites[0]?.siteId ?? null);
    }).catch(() => { if (active) setSitesError(true); });
    return () => { active = false; };
  }, [siteId]);
  useEffect(() => {
    const unsubscribe = onSensitiveMemoryCacheClear(() => {
        visibleReadKey.current = null;
        setData(null);
        setBusy(false);
      });
    return () => {
      unsubscribe();
    };
  }, []);
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    panel.current?.querySelector<HTMLButtonElement>('button')?.focus();
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopImmediatePropagation();
        onClose();
      }
      if (e.key === 'Tab') {
        const elements = [
          ...(panel.current?.querySelectorAll<HTMLElement>(
            'button:not(:disabled),input,select,[tabindex="0"]',
          ) ?? []),
        ];
        const first = elements[0],
          last = elements.at(-1);
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last?.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first?.focus();
        }
      }
    };
    document.addEventListener('keydown', handler, true);
    return () => {
      document.removeEventListener('keydown', handler, true);
      document.body.style.overflow = overflow;
      previous?.focus();
    };
  }, [onClose]);
  useEffect(() => {
    let active = true;
    if (!selectedSiteId) { setBusy(false); setData(null); return; }
    const key = cacheKey(uid, selectedSiteId, month);
    const cached = peekMonthlyMeeting(uid, selectedSiteId, month);
    const force = revision !== lastHandledRevision.current;
    lastHandledRevision.current = revision;
    visibleReadKey.current = cached ? key : null;
    setData(cached);
    setBusy(true);
    void loadMonthlyMeeting(uid, selectedSiteId, month, force)
      .then((value) => {
        if (active && peekMonthlyMeeting(uid, selectedSiteId, month) === value) {
          visibleReadKey.current = key;
          setData(value);
        }
      })
      .catch(() => {
        if (active) setData((current) => (visibleReadKey.current === key ? current : null));
      })
      .finally(() => {
        if (active) setBusy(false);
      });
    return () => {
      active = false;
    };
  }, [uid, selectedSiteId, month, revision]);
  const value = data?.month === month && visibleReadKey.current === cacheKey(uid, selectedSiteId, month) ? data : null;
  const records = value?.meetings.value?.records ?? [];
  const meeting = records.find((r) => r.id === meetingId) ?? records[0];
  const operations = value?.operations.value,
    inspection = value?.inspection.value;
  const open = (id: string, label: string) =>
    featureIds.includes(id) ? (
      <Button
        variant="quiet"
        type="button"
        className="hq-link"
        onClick={() => {
          onClose();
          onOpen(id);
        }}
      >
        {label} →
      </Button>
    ) : null;
  const note = (text: string) => <p className="hq-meeting-note">{text}</p>;
  const status = (state: string | undefined) =>
    state === 'unavailable'
      ? note('자료를 불러오지 못했습니다. 조회 권한과 자료 연결 상태를 확인해 주세요.')
      : null;
  const meetingText = (title: string, text: string | undefined) => (
    <section className="hq-note">
      <h3>{title}</h3>
      <p>{text?.trim() || '등록된 내용이 없습니다.'}</p>
    </section>
  );
  const body = () => {
    switch (section) {
      case 'summary':
        return (
          <>
            <MeetingTable
              headings={['보고 분야', '선택한 월의 현황', '확인']}
              rows={[
                ['계획 이행', meeting?.agenda ? '회의 안건 등록' : '계획과 실시 결과 확인', 'plan'],
                [
                  '위험요인·개선',
                  `점검 ${count(inspection?.inspections)}회 · 지적 ${count(inspection?.findings)}건`,
                  'risk',
                ],
                [
                  '예산 집행',
                  value?.budget.value
                    ? `KOP 자체 비용 ${count(Math.round(value.budget.value.aggregates.principal / 10000))}만원`
                    : '확인 필요',
                  'budget',
                ],
                [
                  '종사자 의견',
                  meeting?.discussion ? '회의 논의 내용 등록' : '의견·반영 결과 확인',
                  'voice',
                ],
                ['사고·후속조치', '사고 관리대장과 후속조치 확인', 'accident'],
                [
                  '교육 현황',
                  operations?.education
                    ? `${count(operations.education.total)}회 · 참석 연인원 ${count(operations.education.attendance)}명`
                    : '확인 필요',
                  'education',
                ],
              ].map(([label, summary, id]) => [
                label,
                summary,
                <Button
                  key={id}
                  variant="quiet"
                  className="hq-link"
                  onClick={() => setSection(id!)}
                >
                  상세 →
                </Button>,
              ])}
            />
            {meetingText('회의 결정사항', meeting?.decisions)}
            {note(
              '실적은 선택한 보고 대상월의 현재 조회 결과입니다. 회의록의 완료 상태와 보고 수치의 확정 여부는 구분합니다.',
            )}
          </>
        );
      case 'risk':
        return (
          <>
            {status(value?.inspection.state)}
            <MeetingTable
              headings={['집계 항목', '현황']}
              rows={[
                ['점검 실시', `${count(inspection?.inspections)}회`],
                ['지적사항', `${count(inspection?.findings)}건`],
                ['조치 완료', count(inspection?.completed)],
                ['미조치', count(inspection?.unresolved)],
                ['결재 대기', count(inspection?.approval)],
                ['담당 재지정', count(inspection?.reassign)],
                ['비일상작업', `${count(operations?.work?.monthly)}건`],
              ]}
            />
            {note(
              '지적사항은 선택한 기간 발생 건의 현재 상태입니다. 비일상작업은 지적 건수와 합산하지 않습니다.',
            )}
            {open('inspection-reporting', '점검·지적 근거 기록')}
            {open('work-permit', '비일상작업 기록')}
          </>
        );
      case 'budget':
        return (
          <>
            {status(value?.budget.state)}
            <MeetingTable
              headings={['비용 구분', '당월 사용액']}
              rows={[
                [
                  'KOP 자체 안전관리 비용',
                  value?.budget.value
                    ? `${count(value.budget.value.aggregates.principal)}원`
                    : '확인 필요',
                ],
              ]}
            />
            {note(
              '협력업체 사용내역은 비용 집행 관리 상세에서 별도로 확인할 수 있습니다.',
            )}
            {open('safety-cost', '비용 집행 관리 상세')}
          </>
        );
      case 'education':
        return (
          <>
            {status(value?.operations.state)}
            <MeetingTable
              headings={['집계 항목', '현황']}
              rows={[
                ['교육 실시', `${count(operations?.education?.total)}회`],
                ['참석 연인원', `${count(operations?.education?.attendance)}명`],
                ['TBM 실시', `${count(operations?.tbm?.count)}회`],
                ['TBM 참석 연인원', `${count(operations?.tbm?.attendance)}명`],
                ['비상훈련 계획', `${count(operations?.drill?.planned)}건`],
                ['비상훈련 결과', `${count(operations?.drill?.completed)}건`],
              ]}
            />
            {note(
              '참석 연인원은 교육 대상자 수나 고유 이수 인원과 다릅니다. 대상자별 이수 여부는 교육 기록에서 확인합니다.',
            )}
            {open('safety-training', '교육별 이수 근거')}
            {open('evacuation-drill', '훈련 결과')}
          </>
        );
      case 'plan':
        return (
          <>
            {meetingText('회의 안건·추진사항', meeting?.agenda)}
            {meetingText('결정사항', meeting?.decisions)}
            {note(
              '월별 계획 대비 실적은 회의 안건과 원 기록을 함께 확인합니다. 문서 수를 계획 이행률로 표시하지 않습니다.',
            )}
            {open('meeting-minutes', '회의록 확인')}
          </>
        );
      case 'voice':
        return (
          <>
            {meetingText('논의·의견청취 내용', meeting?.discussion)}
            {meetingText('반영 결과·결정사항', meeting?.decisions)}
            {note('회의 논의 내용입니다. 근로자 의견의 전체 접수·반영 건수로 집계하지 않습니다.')}
            {open('regular-risk', '위험성평가 근로자 의견')}
          </>
        );
      case 'accident':
        return (
          <>
            {note(
              '당월 발생·연간 누계는 사고 관리대장을 기준으로 확인합니다. 집계가 연결되지 않은 값을 0건으로 표시하지 않습니다.',
            )}
            {meetingText('관련 회의 결정사항', meeting?.decisions)}
            {open('incident', '사고·후속조치 기록')}
          </>
        );
      case 'cases':
        return (
          <>
            {note(
              '주요 개선사례는 기존 지적사항의 개선 전후 사진과 조치 결과를 기준으로 검토합니다. 현재 회의에 선정한 개선사례 목록은 별도로 등록되지 않았습니다.',
            )}
            {meetingText('회의에 기록된 개선 내용', meeting?.discussion)}
            {open('inspection-reporting', '개선 전후 근거 기록')}
          </>
        );
      case 'actions':
        return (
          <>
            {status(value?.meetings.state)}
            {meeting?.followUps.length ? (
              <MeetingTable
                headings={['후속조치', '담당', '기한', '상태']}
                rows={meeting.followUps.map((item) => [
                  item.content,
                  item.owner ?? '확인 필요',
                  item.dueDate ?? '확인 필요',
                  item.status === 'done' ? '완료' : '진행 중',
                ])}
              />
            ) : (
              note('선택한 회의에 등록된 후속조치가 없습니다.')
            )}
            {open('meeting-minutes', '회의록·후속조치 관리')}
          </>
        );
      default:
        return null;
    }
  };
  return createPortal(
    <div className="hq-meeting-layer">
      <article
        ref={panel}
        className="hq-meeting-dialog glass-surface"
        role="dialog"
        aria-modal="true"
        aria-labelledby="hq-meeting-title"
      >
        <header>
          <h2 id="hq-meeting-title">안전보건 월회의</h2>
          <Button
            variant="quiet"
            size="compact"
            icon={<X size={16} />}
            aria-label="닫기"
            title="닫기"
            onClick={onClose}
          />
        </header>
        <div className="hq-meeting-toolbar">
          {!siteId ? <label>사업장{' '}
            <select className="home-control" aria-label="월회의 사업장" value={selectedSiteId ?? ''}
              onChange={(event) => { setMeetingId(''); setData(null); setSelectedSiteId(event.target.value || null); }}>
              {!siteChoices.length ? <option value="">{sitesError ? '사업장 목록을 불러오지 못했습니다' : '사업장 목록을 불러오는 중'}</option> : null}
              {siteChoices.map((site) => <option key={site.siteId} value={site.siteId}>{site.displayName}</option>)}
            </select>
          </label> : null}
          <label>
            보고 대상월{' '}
            <input
              type="month"
              className="home-control"
              value={month}
              onChange={(e) => {
                if (e.target.value) setMonth(e.target.value);
              }}
            />
          </label>
          <label>
            회의록{' '}
            <select
              className="home-control"
              value={meeting?.id ?? ''}
              onChange={(e) => setMeetingId(e.target.value)}
            >
              {!records.length && (
                <option value="">
                  {value?.meetings.state === 'unavailable'
                    ? '회의록 조회 확인 필요'
                    : '등록된 월간 회의록 없음'}
                </option>
              )}
              {records.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.date} · {r.title}
                </option>
              ))}
            </select>
          </label>
          <Button
            variant="quiet"
            size="compact"
            icon={<RefreshCw size={16} />}
            aria-label="월회의 자료 새로고침"
            title="월회의 자료 새로고침"
            disabled={busy}
            onClick={() => setRevision((n) => n + 1)}
          />
        </div>
        <div className="hq-meeting-layout">
          <nav aria-label="월회의 보고 항목">
            {sections.map(([id, label]) => (
              <Button
                variant="quiet"
                key={id}
                aria-current={section === id ? 'page' : undefined}
                onClick={() => setSection(id)}
              >
                {label}
              </Button>
            ))}
          </nav>
          <div className="hq-meeting-content" data-bounded-content>
            {busy && !value ? (
              <FeatureLoading label="월회의 자료 불러오는 중" />
            ) : (
              <>
                <h3>{sections.find(([id]) => id === section)?.[1]}</h3>
                {note(`${siteChoices.find((site) => site.siteId === selectedSiteId)?.displayName ?? (selectedSiteId === 'site-eumseong' ? '음성공장' : selectedSiteId ?? '사업장 미선택')} · ${month} 실적${meeting ? ` · 회의일 ${meeting.date}` : ''}`)}
                {!value ? (
                  <p role="alert">자료를 불러오지 못했습니다. 새로고침해 주세요.</p>
                ) : (
                  body()
                )}
              </>
            )}
          </div>
        </div>
        <footer>
          {meeting
            ? `회의록 ${meeting.status === 'completed' ? '작성 완료' : '작성 중'} · 보고 수치는 현재 조회 기준`
            : '회의록 등록 상태와 월별 실적은 별도로 확인합니다.'}
          {value?.meetings.value?.nextCursor ? ' · 최근 회의록 30건 표시' : ''}
        </footer>
      </article>
    </div>,
    document.body,
  );
}
